/**
 * Single vs bundle pack toggle for expanded PDP: hides main variant picker and primary buy buttons,
 * shows bundle copy and a secondary product form (Shopify bundle SKU).
 */
class ExpandedBundleToggle extends HTMLElement {
  /** @type {'single' | 'bundle'} */
  #mode = 'single';

  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    const initial = this.dataset.initialMode === 'bundle' ? 'bundle' : 'single';
    this.#bindTabs(signal);
    this.setMode(initial, false);
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = undefined;
  }

  #productDetailsRoot() {
    return this.closest('[data-testid="product-information-details"]');
  }

  #mainVariantPicker() {
    return this.#productDetailsRoot()?.querySelector('variant-picker');
  }

  #mainBuyButtons() {
    return this.#productDetailsRoot()?.querySelector('.buy-buttons-block');
  }

  /**
   * @param {AbortSignal} signal
   */
  #bindTabs(signal) {
    /** @param {MouseEvent} e */
    const onTabClick = (e) => {
      const btn = e.target instanceof Element ? e.target.closest('[data-mode]') : null;
      if (!(btn instanceof HTMLButtonElement)) return;
      e.stopPropagation();
      const mode = btn.dataset.mode;
      if (mode === 'single' || mode === 'bundle') this.setMode(mode, true);
    };

    for (const btn of this.querySelectorAll('[data-mode]')) {
      btn.addEventListener('click', onTabClick, { capture: true, signal });
    }

    this.addEventListener(
      'keydown',
      (e) => {
        if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
        e.preventDefault();
        if (e.key === 'ArrowLeft') this.setMode('single', true);
        if (e.key === 'ArrowRight') this.setMode('bundle', true);
      },
      { signal }
    );
  }

  /**
   * @param {'single' | 'bundle'} mode
   * @param {boolean} moveFocus
   */
  setMode(mode, moveFocus) {
    this.#mode = mode;
    const picker = this.#mainVariantPicker();
    const buy = this.#mainBuyButtons();
    const panel = this.querySelector('[data-expanded-bundle-panel="bundle"]');

    const isBundle = mode === 'bundle';

    if (picker) {
      picker.toggleAttribute('hidden', isBundle);
      picker.style.display = isBundle ? 'none' : '';
      if (isBundle) {
        picker.setAttribute('inert', '');
      } else {
        picker.removeAttribute('inert');
      }
    }
    if (buy) {
      buy.toggleAttribute('hidden', isBundle);
      buy.style.display = isBundle ? 'none' : '';
      if (isBundle) {
        buy.setAttribute('inert', '');
      } else {
        buy.removeAttribute('inert');
      }
    }
    if (panel) {
      panel.hidden = !isBundle;
      if (isBundle) {
        panel.removeAttribute('inert');
      } else {
        panel.setAttribute('inert', '');
      }
    }

    for (const btn of this.querySelectorAll('[data-mode]')) {
      if (!(btn instanceof HTMLButtonElement)) continue;
      const selected = btn.dataset.mode === mode;
      btn.setAttribute('aria-selected', selected ? 'true' : 'false');
      btn.tabIndex = selected ? 0 : -1;
      if (moveFocus && selected) btn.focus();
    }

    if (isBundle) {
      document.documentElement.setAttribute('data-expanded-bundle-mode', 'bundle');
    } else {
      document.documentElement.removeAttribute('data-expanded-bundle-mode');
    }
  }
}

if (!customElements.get('expanded-bundle-toggle')) {
  customElements.define('expanded-bundle-toggle', ExpandedBundleToggle);
}
